import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { Role } from "@alzadrach/types";
import { apiClient, setToken } from "../api/client";

/** sessionStorage key for the admin "view as member" preview (survives reloads, not tabs). */
const VIEW_AS_MEMBER_KEY = "alzadrach.viewAsMember";

function readViewAsMember(): boolean {
  try {
    return sessionStorage.getItem(VIEW_AS_MEMBER_KEY) === "1";
  } catch {
    return false;
  }
}

function writeViewAsMember(value: boolean): void {
  try {
    if (value) sessionStorage.setItem(VIEW_AS_MEMBER_KEY, "1");
    else sessionStorage.removeItem(VIEW_AS_MEMBER_KEY);
  } catch {
    /* storage unavailable (private mode, etc.) — preview just won't persist */
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthUser {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  /** Computed: `${firstName} ${lastName}` */
  fullName: string;
  role: Role;
  /**
   * "ninos": a solo niños socio this año (not participating, but their niños
   * are) — only the dashboard and the perfil. Missing = "full".
   */
  access?: "full" | "ninos";
  avatar?: string | null;
  phone?: string | null;
  partner: { id: string; fullName: string } | null;
}

export interface AuthContextValue {
  /**
   * The logged-in user as the UI should treat them. While an admin previews
   * the web as a socio, `user.role` is MEMBER here even though the session
   * (and every API call) stays admin.
   */
  user: AuthUser | null;
  loading: boolean;
  /** True when the real session role is ADMIN, regardless of the preview. */
  isActualAdmin: boolean;
  /** Admin-only preview: render the whole app as a socio would see it. */
  viewAsMember: boolean;
  setViewAsMember: (value: boolean) => void;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Re-fetch /me (e.g. after the member updates their avatar). */
  refreshUser: () => Promise<void>;
  /**
   * adoptToken — called by the passkey flow after it obtains an accessToken
   * externally (WebAuthn verify step). Sets the token in the API client and
   * fetches /me to populate the user state, completing the session.
   */
  adoptToken: (accessToken: string) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewAsMember, setViewAsMemberState] = useState<boolean>(readViewAsMember);

  const setViewAsMember = useCallback((value: boolean) => {
    writeViewAsMember(value);
    setViewAsMemberState(value);
  }, []);

  const isActualAdmin = user?.role === Role.ADMIN;
  // The preview only applies to admins; a member's user object is untouched.
  const effectiveUser = user && isActualAdmin && viewAsMember ? { ...user, role: Role.MEMBER } : user;

  /** Fetch current user from /me — called after login or on mount to restore. */
  const fetchMe = useCallback(async (): Promise<void> => {
    try {
      const me = await apiClient.request<AuthUser>("/me");
      setUser({
        ...me,
        avatar: me.avatar ?? null,
        phone: me.phone ?? null,
        partner: me.partner ?? null,
      });
    } catch {
      setUser(null);
    }
  }, []);

  /** On mount: attempt a silent token refresh to restore the session (uses the
   *  httpOnly refresh cookie set by the server). If it succeeds we fetch /me. */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(
          `${
            (import.meta as { env?: { VITE_API_BASE?: string } }).env
              ?.VITE_API_BASE ?? "/api"
          }/auth/refresh`,
          { method: "POST" }
        );
        if (!cancelled && res.ok) {
          const data = (await res.json()) as { accessToken: string };
          setToken(data.accessToken);
          await fetchMe();
        }
      } catch {
        // No valid session — stay logged out
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchMe]);

  const login = useCallback(
    async (identifier: string, password: string): Promise<void> => {
      await apiClient.login(identifier, password);
      await fetchMe();
    },
    [fetchMe]
  );

  const logout = useCallback(async (): Promise<void> => {
    await apiClient.logout();
    setUser(null);
    setViewAsMember(false);
  }, [setViewAsMember]);

  const adoptToken = useCallback(
    async (accessToken: string): Promise<void> => {
      setToken(accessToken);
      await fetchMe();
    },
    [fetchMe]
  );

  return (
    <AuthContext.Provider
      value={{
        user: effectiveUser,
        loading,
        isActualAdmin,
        viewAsMember: isActualAdmin && viewAsMember,
        setViewAsMember,
        login,
        logout,
        adoptToken,
        refreshUser: fetchMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}
